import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {PersonalService} from '../personal.service';
import {MatPaginator} from '@angular/material/paginator';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Employee} from '../../admin/models/employee.model';
import {Vacation} from '../../admin/models/vacation.model';
import {FabMessageService} from '@fabric/components/message';
import {FormControl, FormGroup, NgForm} from '@angular/forms';
import {AuthenticationService} from '../../core/authentication/authentication.service';
import {isWeekend} from 'date-fns';
import {HttpErrorResponse} from '@angular/common/http';
import { HrService } from '../../hr/hr.service';

@Component({
    selector: 'app-personal-pop-up',
    templateUrl: './personal-pop-up.component.html',
    styleUrls: ['./personal-pop-up.component.scss']
})
export class PersonalPopUpComponent implements OnInit {

    public employee = {} as Employee;
    public model = {} as Vacation;
    public daysSelected: any[] = [];
    public vacation = {} as Vacation;
    public vacationOptions = [{value: 'HOLIDAY', displayValue: 'Holiday'}, {
        value: 'WORK_FROM_HOME',
        displayValue: 'Work from Home'
    },
        {value: 'HOSPITAL_APPOINTMENT', displayValue: 'Hospital Appointment'}, {
            value: 'DAY_IN_LIEU',
            displayValue: 'Day-in-Lieu'
        }];

    public startEndRange = new FormGroup({
        start: new FormControl<Date | null>(null),
        end: new FormControl<Date | null>(null),
    });
    public modifyRequest: boolean = false;
    public globalStart = '';
    public globalEnd = '';
    public halfDayAM = false;
    public halfDayPM = false;
    public halfDayCorrect = true;
    public isHospitalDayCorrect = true;
    public isPageLoading = false;
    public thisYear: any[] = [];
    public nextYear: any[] = [];
    public formattedStartDate: string = '';
    public formattedEndDate: string = '';
    public publicHolidays: any[] = [];
    public makeWeekendUnselectable = (d: any): boolean => {
        const day = d.getDay();
        return day !== 0 && day !== 6;
    }

    public minVacationDate = new Date('01/02/2023');
    public maxWorkAbroadDate = new Date();
    public isDuplicated = false;

    @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
    @ViewChild('form') private readonly form!: NgForm;

    constructor(private readonly personalService: PersonalService,
                private readonly fabMessageService: FabMessageService,
                private authService: AuthenticationService,
                private readonly hrService: HrService,
                public dialogRef: MatDialogRef<[]>,
                @Inject(MAT_DIALOG_DATA) public selectedData: any) {
    }

    ngOnInit(): void {
        this.employee = this.selectedData.employee;
        this.vacation = this.selectedData.vacation;
        this.halfDayCheck();
        if (this.vacation === null) {
            this.model.updatedBy = this.authService.userInfo.gmin || '';
            this.model.employeeId = this.employee.id;
            this.model.region = this.employee.region;
            this.model.absenceType = this.vacationOptions[0].value;
            this.model.halfDay = false;
            this.model.requestStatus = 'PENDING';
        } else {
            this.model = this.selectedData.vacation;
            this.formattedStartDate = this.formatDate(this.vacation.startDate);
            this.formattedEndDate = this.formatDate(this.vacation.endDate);
            this.setGlobalDates();
            this.startEndRange.patchValue({start: new Date(this.model.startDate), end: new Date(this.model.endDate)});
            this.modifyRequest = true;
        }
        this.maxWorkAbroadDate = this.personalService.convertDateToNextYearEnd(this.maxWorkAbroadDate);
        this.hrService.getPublicHoliday(this.employee.region).subscribe({
            next: (value) => {
                value.forEach((d: any) => {
                    this.publicHolidays.push(new Date(d.holidayDate.slice(6,10), d.holidayDate.slice(3,5) - 1, d.holidayDate.slice(0,2)));
                });
            }
        });
    }

    /**
     * Closes the popup
     */
    close(): void {
        this.dialogRef.close();
    }

    /**
     * Takes a string, number or Date object as input and
     * returns a formatted date string in the format 'DD/MM/YYYY'.
     * It pads the day and month with a leading zero if necessary
     * @param dateString
     */
    formatDate(dateString: string | number | Date) {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    /**
     * Check if vacation clicked on is a half day or not.
     * If is a half day, determine whether AM/PM and set checkbox based on this
     */
    halfDayCheck() {
        if (this.vacation) {
            if (this.vacation.halfDayType === 'AM') {
                this.halfDayAM = true;
                this.halfDayPM = false;
            } else if (this.vacation.halfDayType === 'PM') {
                this.halfDayAM = false;
                this.halfDayPM = true;
            }
        } else {
            this.halfDayAM = false;
            this.halfDayPM = false;
        }
    }

    /**
     * Set PM half day to false when selecting AM
     */
    deSelectPM() {
        this.halfDayPM = false;
    }

    /**
     * Set AM half day to false when selecting PM
     */
    deSelectAM() {
        this.halfDayAM = false;
    }

    /**
     * Sets values for globalStart and globalEnd date by getting substrings of the appropriate model fields
     */
    public setGlobalDates(): void {
        this.globalStart = this.model.startDate.substring(3, 5) + '/' + this.model.startDate.substring(0, 2) + '/' + this.model.startDate.substring(6);
        this.globalEnd = this.model.endDate.substring(3, 5) + '/' + this.model.endDate.substring(0, 2) + '/' + this.model.endDate.substring(6);
    }

    /**
     * Checks that the user has enough holidays to make a request
     */
    public checkHolidayAllowance() {
        let pendingAllowance = 0;
        if (!this.startEndRange.value || !this.startEndRange.value.start || !this.startEndRange.value.end) {
            return false;
        }
        if (this.model.absenceType === 'DAY_IN_LIEU' || this.model.absenceType === 'WORK_FROM_HOME' || this.model.absenceType === 'HOSPITAL_APPOINTMENT') {
            return true;
        }
        this.model.numberOfAnnualLeaveDays = Math.floor((this.startEndRange.value.end!.getTime() - this.startEndRange.value.start!.getTime()) / 1000 / 60 / 60 / 24 + 1);
        for (let day = new Date(this.startEndRange.value.start!.getTime()); day <= new Date(this.startEndRange.value.end!.getTime()); day.setDate(day.getDate() + 1)) {
            if (isWeekend(day)) {
                this.model.numberOfAnnualLeaveDays--;
            }
        }
        this.employee.employeeVacationAllowance.forEach((i) => {
            if (this.startEndRange.value.end!.getFullYear() == i.year) {
                pendingAllowance = i.vacationAllowanceRemaining;
            }
        });
        if (this.halfDayAM || this.halfDayPM) {
            return pendingAllowance > 0 && pendingAllowance >= (this.model.numberOfAnnualLeaveDays * 0.5);
        }
        let startCopy = new Date(this.startEndRange.value.start.getTime());
        let endCopy = new Date(this.startEndRange.value.end.getTime());
        let days = this.getDateArray(startCopy, endCopy);
        this.publicHolidays.forEach((d) => {
           days.forEach((v) => {
               if (v.getTime() == d.getTime()) {
                   pendingAllowance++;
               }
           });
        });
        return pendingAllowance > 0 && pendingAllowance >= this.model.numberOfAnnualLeaveDays;
    }

    /**
     * Submits a request to the api to create or modify a vacation
     */
    public submitForm(): void {
        this.checkYearEnd();
        this.setHalfDayType();
        this.setStartAndEndDate();
        this.checkHospitalDays();
        this.vacationAlreadyExists();
        this.ifNoReasonGiven();
        this.isPageLoading = true;
        if (this.modifyRequest && this.halfDayCorrect) {
            this.personalService.modifyVacationRequest(this.model).subscribe({
                next: () => {
                    this.fabMessageService.displayMessage('Request submitted successfully');
                    this.model = this.vacation;
                    this.isPageLoading = false;
                    this.close();
                }, error: (err: HttpErrorResponse) => {
                    this.fabMessageService.displayErrorMessage(err.error.errorMessage);
                    this.isPageLoading = false;
                }
            });
        } else if (!this.halfDayCorrect) {
            this.isPageLoading = false;
            this.fabMessageService.displayErrorMessage('Cannot select more than one half day in a request');
        } else if (!this.isHospitalDayCorrect) {
            this.isPageLoading = false;
            this.fabMessageService.displayErrorMessage('Cannot select more than one hospital day in a request');
        }
        else if (this.isDuplicated) {
            this.isPageLoading = false;
            this.fabMessageService.displayErrorMessage('Absence Already Exists For Dates Requested');
        } else {
            this.personalService.submitVacationRequest(this.model).subscribe({
                next: () => {
                    this.fabMessageService.displayMessage('Request submitted successfully');
                    this.isPageLoading = false;
                    if (this.nextYear.length > 0) {
                        this.submitForm();
                    }
                    this.close();
                }, error: (err: HttpErrorResponse) => {
                    this.fabMessageService.displayErrorMessage(err.error.errorMessage);
                    this.isPageLoading = false;
                }
            });
        }
    }

    public ifNoReasonGiven() {
        if (this.model.absenceReason === undefined) {
            this.model.absenceReason = 'Holiday Request'
        }
    }

    /**
     * Check is more than one day is selected in a hospital appointment request
     */
    public checkHospitalDays() {
        this.isHospitalDayCorrect = !(this.model.startDate !== this.model.endDate && this.model.absenceType === 'HOSPITAL_APPOINTMENT');
    }

    /**
     * Set Start and End date for the vacation request
     */
    public setStartAndEndDate() {
        let startDate = this.model.startDate;
        let endDate = this.model.endDate;

        startDate = this.personalService.swapDateAndMonth(startDate);
        endDate = this.personalService.swapDateAndMonth(endDate);

        this.startEndRange.value.start!.setDate(new Date(startDate).getDate());
        this.startEndRange.value.start!.setMonth(new Date(startDate).getMonth());
        this.startEndRange.value.start!.setFullYear(new Date(startDate).getUTCFullYear());

        this.startEndRange.value.end!.setDate(new Date(endDate).getDate());
        this.startEndRange.value.end!.setMonth(new Date(endDate).getMonth());
        this.startEndRange.value.end!.setFullYear(new Date(endDate).getUTCFullYear());
    }

    /**
     * If a holiday request is split over 2 years, we divide the call up into 2, current year and next year
     * Then send current year request first and come back after to send next years request.
     */
    checkYearEnd() {
        this.populateDaysSelected();
        const d = new Date();
        const currentYear = d.getFullYear();
        this.halfDayCorrect = true;
        if (this.nextYear.length === 0) {
            for (let i = 0; i < this.daysSelected.length; i++) {
                if (this.daysSelected[i].getFullYear() === currentYear) {
                    this.thisYear.push(this.daysSelected[i]);
                    this.daysSelected.splice(i, 1);
                    i--;
                } else {
                    this.nextYear.push(this.daysSelected[i]);
                    this.daysSelected.splice(i, 1);
                    i--;
                }
            }
        }
        if (this.thisYear.length > 0) {
            this.model.startDate = this.thisYear[0]!.toString();
            this.model.endDate = this.thisYear[this.thisYear.length - 1]!.toString();
            this.model.startDate = this.personalService.convertDateFormatForAPI(this.model.startDate);
            this.model.endDate = this.personalService.convertDateFormatForAPI(this.model.endDate);
            this.model.numberOfAnnualLeaveDays = this.thisYear.length;
            this.thisYear = [];
            if ((this.model.startDate !== this.model.endDate) && (this.halfDayAM || this.halfDayPM)) {
                this.halfDayCorrect = false;
            }
        } else if (this.nextYear.length > 0) {
            this.model.startDate = this.nextYear[0]!.toString();
            this.model.endDate = this.nextYear[this.nextYear.length - 1]!.toString();
            this.model.startDate = this.personalService.convertDateFormatForAPI(this.model.startDate);
            this.model.endDate = this.personalService.convertDateFormatForAPI(this.model.endDate);
            this.model.numberOfAnnualLeaveDays = this.nextYear.length;
            if ((this.model.startDate !== this.model.endDate) && (this.halfDayAM || this.halfDayPM)) {
                this.halfDayCorrect = false;
            }
            this.nextYear = [];
        }
    }

    /**
     * Populate the days selected array from the date range picked
     */
    populateDaysSelected() {
        const startDate = this.startEndRange.value.start;
        const endDate = this.startEndRange.value.end;
        this.daysSelected = this.getDateArray(startDate, endDate);
        for (let i = 0; i < this.daysSelected.length; i++) {
            if (isWeekend(this.daysSelected[i])) {
                this.daysSelected.splice(i, 1);
                i--;
            }
        }
    }

    /**
     * Returns an array of all dates between a start and end date inclusive.
     * @param start - the start date
     * @param end - the end date
     */

    getDateArray(start: Date | null | undefined, end: Date | null | undefined) {
        let arr = [];
        let dt = start;
        if (dt && end) {
            while (dt <= end) {
                arr.push(new Date(dt));
                dt.setDate(dt.getDate() + 1);
            }
        }
        return arr;
    }

    /**
     * Set the half day type values in the model before submitting a save or update.
     */
    setHalfDayType() {
        if (this.halfDayAM) {
            this.model.halfDayType = 'AM'
            this.model.halfDay = true;
        } else if (this.halfDayPM) {
            this.model.halfDayType = 'PM'
            this.model.halfDay = true;
        } else {
            this.model.halfDayType = 'FULL'
            this.model.halfDay = false;
        }
    }

    public stripWhiteSpace(): void {
        if (this.model.absenceReason) {
            this.model.absenceReason = this.model.absenceReason.trim();
            this.model.absenceReason = this.model.absenceReason.replace(/ +/g, ' ');
        }
    }

    /**
     * Duplication check if a vacation request already exists for dates.
     * Checks all dates between the start and end date of an already existing request
     */
    public vacationAlreadyExists() {
        for (const element of this.employee.vacation) {
            if (element.requestStatus !== 'CANCELLED' && element.requestStatus !== 'DECLINED') {
                if(element.halfDayType === this.model.halfDayType && this.formatDate(element.startDate) === this.model.startDate) {
                    this.isDuplicated = true;
                    return false;
                }
                if (((element.halfDayType === 'PM' && this.model.halfDayType === 'AM') || (element.halfDayType === 'AM' && this.model.halfDayType === 'PM'))
                        && this.formatDate(element.startDate) === this.model.startDate){
                    this.isDuplicated = false;
                    break;
                }
                const dateFrom = this.formatDate(element.startDate);
                const dateTo = this.formatDate(element.endDate);
                const dateCheckStart = this.model.startDate;
                const dateCheckEnd = this.model.endDate;

                const d1 = dateFrom.split('/');
                const d2 = dateTo.split('/');
                const c1 = dateCheckStart.split('/');
                const c2 = dateCheckEnd.split('/');

                const from = new Date(Number(d1[2]), parseInt(d1[1]) - 1, Number(d1[0]));
                const to = new Date(Number(d2[2]), parseInt(d2[1]) - 1, Number(d2[0]));
                const checkStart = new Date(Number(c1[2]), parseInt(c1[1]) - 1, Number(c1[0]));
                const checkEnd = new Date(Number(c2[2]), parseInt(c2[1]) - 1, Number(c2[0]));
                if (checkStart >= from && checkStart <= to) {
                    this.isDuplicated = checkStart >= from && checkStart <= to;
                    return false;
                }
                else if (checkEnd >= from && checkEnd <= to) {
                    this.isDuplicated = true;
                    return false;
                }
                else if (checkEnd.getTime() == from.getTime()) {
                    this.isDuplicated = true;
                    return false;
                }
            }
        }
    }
}
